import formatPhoneForUser from './formatPhoneForUser';

const STORAGE_KEY = 'recentConversations';
const MAX_PER_BUSINESS = 3;

export interface RecentConversation {
	_id: string;
	_business: string;
	customerName: string;
	lastMessagePreview: string;
	visitedAt: number;
}

interface ConversationLike {
	_id: string;
	_business: string;
	_customer?: { name?: string; phone?: string } | null;
	messages?: Array<{ text?: string }> | null;
}

interface AuthLike {
	_id?: string;
	user?: { isEnterpriseEnabled?: boolean } | null;
}

function readAll(): RecentConversation[] {
	try {
		const raw = localStorage.getItem(STORAGE_KEY);
		if (!raw) return [];
		const parsed = JSON.parse(raw);
		return Array.isArray(parsed) ? parsed : [];
	} catch {
		return [];
	}
}

function writeAll(entries: RecentConversation[]): void {
	try {
		localStorage.setItem(STORAGE_KEY, JSON.stringify(entries));
	} catch {
		// ignore localStorage errors (quota, private mode, etc.)
	}
}

function buildEntry(conversation: ConversationLike): RecentConversation | null {
	if (!conversation?._id || !conversation?._business) return null;

	const customer = conversation._customer;
	const customerName =
		customer?.name ||
		(customer?.phone && formatPhoneForUser(customer.phone)) ||
		'No name';

	const messages = conversation.messages || [];
	const lastMsg = messages[messages.length - 1];
	const lastMessagePreview = lastMsg?.text?.slice(0, 80) || '';

	return {
		_id: conversation._id,
		_business: conversation._business,
		customerName,
		lastMessagePreview,
		visitedAt: Date.now(),
	};
}

/**
 * Adds a conversation to the recent list. Caps to MAX_PER_BUSINESS entries
 * per `_business` so heavy use of one location does not evict another's
 * history. Existing entries for the same `_id` (regardless of business) are
 * removed before insertion so a re-visit refreshes the timestamp.
 */
export function addRecentConversation(conversation: ConversationLike): void {
	const entry = buildEntry(conversation);
	if (!entry) return;

	const existing = readAll().filter((c) => c._id !== entry._id);
	const sameBusiness: RecentConversation[] = [entry];
	const otherBusiness: RecentConversation[] = [];

	for (const c of existing) {
		if (c._business === entry._business) {
			sameBusiness.push(c);
		} else {
			otherBusiness.push(c);
		}
	}

	const trimmed = sameBusiness
		.sort((a, b) => b.visitedAt - a.visitedAt)
		.slice(0, MAX_PER_BUSINESS);

	writeAll([...trimmed, ...otherBusiness]);
}

/**
 * Returns recent conversations relevant to the current auth context, sorted
 * by most-recently visited and capped to MAX_PER_BUSINESS for display.
 *
 * - Specific location (`auth._id`): returns only entries matching that
 *   `_business`.
 * - Enterprise / "All Locations" mode (`auth.user.isEnterpriseEnabled`):
 *   returns the most recent across all businesses.
 */
export function getRecentConversations(
	auth: AuthLike | null | undefined,
): RecentConversation[] {
	const all = readAll();
	const isEnterprise = !!auth?.user?.isEnterpriseEnabled;

	const filtered = isEnterprise
		? all
		: auth?._id
			? all.filter((c) => c._business === auth._id)
			: [];

	return filtered
		.sort((a, b) => b.visitedAt - a.visitedAt)
		.slice(0, MAX_PER_BUSINESS);
}

export function clearRecentConversations(): void {
	try {
		localStorage.removeItem(STORAGE_KEY);
	} catch {
		// ignore
	}
}
